import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { routes } from '@/constants';
import { usePostAdditionalInfo } from '@/api/services';
import { useNavigate } from '@/hooks/useNavigate';
import { useQuestionsAnswers } from '@/hooks/useQuestionsAnswers';
import { useResidency, type ResidencyAnswer } from '@/hooks/useResidency';
import InAppBrowserBlock from '@/pages/Answers/InAppBrowserBlock.tsx';
import ResidencyBlocked from './ResidencyBlocked';
import ResidencyQuestion from './ResidencyQuestion';
import { rejectedZipMessage } from './zipError';

/**
 * The residency gate, shown between the intro video and the first question.
 *
 * Video answers are biometric data under Illinois BIPA and California
 * CCPA/CPRA, and we are not set up to collect them from residents of those
 * states — so declaring residency there is a dead end.
 *
 * The gate doubles as the fallback ZIP capture: candidates who came through
 * the qr-apply form already gave one, but those invited any other way have
 * `zip_code: null` on their status, and this is the last screen before
 * recording where asking costs nothing.
 */
export default function Residency() {
  const navigate = useNavigate();
  const { answer, setAnswer } = useResidency();
  const { invitationKey, remoteStatus, isLoading } = useQuestionsAnswers();
  const { mutateAsync: saveZip, isLoading: isSavingZip } =
    usePostAdditionalInfo();

  const [zip, setZip] = useState('');
  const [zipError, setZipError] = useState<string | null>(null);

  // Only ask when there is a lead to attach the answer to and the status has
  // actually told us the ZIP is missing. An unresolved status must not be read
  // as "no ZIP" — that would ask everyone who reloads the page.
  const askZip =
    !!invitationKey && !isLoading && !!remoteStatus && !remoteStatus.zip_code;

  // Distinguishes "the candidate just tapped Continue into the block screen"
  // from "the block screen mounted already-blocked" (reload, back-navigation
  // with ca_il already in sessionStorage). Only the former should steal
  // focus onto the block heading — see ResidencyBlocked's `focusHeadingOnMount`.
  const [justBlocked, setJustBlocked] = useState(false);

  const handleSubmit = async (next: ResidencyAnswer) => {
    if (askZip && invitationKey) {
      try {
        await saveZip({ invitation_key: invitationKey, zip_code: zip });
      } catch (error) {
        const rejection = rejectedZipMessage(error);
        // The backend refused this ZIP: it is the only thing on the screen the
        // candidate can fix, so say so and stay put.
        if (rejection) {
          setZipError(rejection);
          return;
        }
        // Anything else — offline, 404, 500 — is not the candidate's problem.
        // Losing a ZIP is cheaper than stranding someone one tap away from
        // the interview they came here to record.
        Sentry.captureException(error);
      }
    }

    setAnswer(next);

    if (next === 'other') {
      navigate(routes.questions);
    } else {
      setJustBlocked(true);
    }
  };

  return (
    <>
      <InAppBrowserBlock />
      {answer === 'ca_il' ? (
        <ResidencyBlocked focusHeadingOnMount={justBlocked} />
      ) : (
        <ResidencyQuestion
          initialAnswer={answer}
          onSubmit={handleSubmit}
          askZip={askZip}
          zip={zip}
          onZipChange={(value) => {
            setZip(value);
            // The rejection described the value that was sent, not this one.
            setZipError(null);
          }}
          zipError={zipError}
          busy={isLoading || isSavingZip}
        />
      )}
    </>
  );
}
